import { createFileRoute } from '@tanstack/react-router'
import { LEGAL_CONFIG, LEGAL_VERSIONS } from '@/legal.config'
import { LegalVersionMeta } from '@/routes/legal/-legal-version-meta'

export const Route = createFileRoute('/legal/_layout/terms')({
  component: TermsOfServicePage,
})

function TermsOfServicePage() {
  return (
    <article className="prose prose-neutral dark:prose-invert max-w-none">
      <h1>Terms of Service</h1>
      <LegalVersionMeta
        current={LEGAL_VERSIONS.terms.current}
        history={LEGAL_VERSIONS.terms.history}
      />

      <p>
        These Terms of Service ("Terms") govern your access to and use of the{' '}
        {LEGAL_CONFIG.productName} platform (the "Service") provided by{' '}
        {LEGAL_CONFIG.companyName}, {LEGAL_CONFIG.companyAddress}
        ("we", "us", "our").
      </p>
      <p>
        By creating an account or using the Service, you agree to these Terms on
        behalf of yourself and the organisation you represent ("Customer").
      </p>

      <h2>1. Service description</h2>
      <p>
        {LEGAL_CONFIG.productName} is an AI-assisted recruitment screening
        platform that enables organisations to conduct structured video, audio,
        and chat interviews with candidates. The Service uses AI models to
        generate competency scores and written rationale. All results are
        intended to assist — not replace — human decision-making in the hiring
        process.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old and authorised to act on behalf of
        your organisation to create an account. The Service is intended for
        business use only.
      </p>

      <h2>3. Account responsibilities</h2>
      <ul>
        <li>
          You are responsible for maintaining the confidentiality of your login
          credentials.
        </li>
        <li>
          You are responsible for all activity under your account, including
          actions taken by team members you invite.
        </li>
        <li>
          You must notify us immediately at{' '}
          <a href={`mailto:${LEGAL_CONFIG.supportEmail}`}>
            {LEGAL_CONFIG.supportEmail}
          </a>{' '}
          if you become aware of any unauthorised access.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use the Service for any unlawful purpose or in violation of applicable
          employment, discrimination, or data protection law.
        </li>
        <li>
          Use AI-generated scores as the sole basis for any hiring, promotion,
          or termination decision without human review.
        </li>
        <li>
          Attempt to circumvent, reverse-engineer, or interfere with the Service
          or its security measures.
        </li>
        <li>
          Upload content that is defamatory, discriminatory, or otherwise
          harmful.
        </li>
        <li>
          Use the Service to assess candidates on prohibited grounds under
          applicable anti-discrimination law (e.g. race, gender, religion,
          disability, age).
        </li>
        <li>
          Resell, sublicense, or otherwise provide access to the Service to
          third parties without our prior written consent.
        </li>
      </ul>

      <h2>5. EU AI Act compliance obligations</h2>
      <p>
        The Service is classified as a high-risk AI system under EU AI Act Annex
        III, Category 4. As a Customer, you acknowledge that:
      </p>
      <ul>
        <li>
          You are the "deployer" of the AI system within the meaning of the EU
          AI Act and are responsible for deployer-side obligations, including
          conducting and documenting a fundamental rights impact assessment
          where required.
        </li>
        <li>
          You must ensure that candidates are informed they are being assessed
          by an AI system before the interview begins. The Service provides a
          transparency notice for this purpose; you must not disable it.
        </li>
        <li>
          You must maintain human oversight over all hiring decisions and must
          not make automated decisions solely on the basis of AI-generated
          output.
        </li>
        <li>
          You must respond to candidate requests for explanation of AI-generated
          scores within a reasonable time (we recommend 30 days).
        </li>
      </ul>

      <h2>6. Data processing</h2>
      <p>
        You are the data controller for personal data processed through the
        Service (including candidate interview data). We act as your data
        processor. Our processing activities, sub-processors, and security
        measures are governed by the{' '}
        <a href="/legal/dpa">Data Processing Agreement</a>, which is
        incorporated into these Terms by reference.
      </p>

      <h2>7. Billing and payment</h2>
      <ul>
        <li>
          Fees are charged in advance based on the plan you select. Pricing is
          displayed at checkout and on your billing page.
        </li>
        <li>
          Payments are processed by our payment processor, Polar. We do not
          store your full payment card details.
        </li>
        <li>
          Subscriptions renew automatically unless cancelled before the renewal
          date. Cancellation takes effect at the end of the current billing
          period.
        </li>
        <li>
          Credit top-ups (where available) are non-refundable unless required by
          applicable law.
        </li>
        <li>
          We reserve the right to modify pricing with 30 days' notice. Continued
          use after the effective date of a price change constitutes acceptance.
        </li>
      </ul>

      <h2>8. Intellectual property</h2>
      <p>
        We own all rights in the Service, including the software, AI models,
        prompts, and generated output templates. You retain ownership of the
        content you upload (job descriptions, screener questions) and the
        candidate data processed through your account.
      </p>
      <p>
        You grant us a limited licence to process your content and candidate
        data solely to provide the Service. We do not use candidate interview
        content to train AI models.
      </p>

      <h2>9. Confidentiality</h2>
      <p>
        Each party agrees to keep the other's confidential information
        confidential and not to disclose it to third parties without prior
        written consent, except as required by law.
      </p>

      <h2>10. Warranties and disclaimers</h2>
      <p>
        We warrant that we will provide the Service with reasonable skill and
        care. The Service is provided "as is" to the maximum extent permitted by
        law. We do not warrant that AI-generated scores are accurate, unbiased,
        or suitable for any particular hiring decision.
      </p>
      <p>
        AI-generated output is a decision-support tool. You remain fully
        responsible for all hiring decisions made using the Service.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by applicable law, our total aggregate
        liability to you for any claims arising under or related to these Terms
        shall not exceed the fees paid by you in the 12 months preceding the
        event giving rise to the claim.
      </p>
      <p>
        Neither party shall be liable for indirect, incidental, consequential,
        or punitive damages, including loss of profit or loss of data.
      </p>

      <h2>12. Termination</h2>
      <p>
        Either party may terminate these Terms on 30 days' written notice. We
        may suspend or terminate your account immediately if you breach these
        Terms or if required by law.
      </p>
      <p>
        Upon termination, we will make your data available for export for 30
        days, after which it will be deleted in accordance with our retention
        policy.
      </p>

      <h2>13. Governing law and disputes</h2>
      <p>
        These Terms are governed by {LEGAL_CONFIG.companyJurisdiction}. Any
        disputes shall first be subject to good-faith negotiation. If not
        resolved within 60 days, disputes shall be finally settled by
        arbitration administered by the Stockholm Chamber of Commerce (SCC)
        under its Arbitration Rules, with the seat of arbitration in{' '}
        {LEGAL_CONFIG.companyArbitrationVenue}.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms. Material changes will be communicated by
        email at least 30 days before taking effect. If you do not agree to the
        changes, you may terminate your subscription before the effective date.
      </p>

      <h2>15. Contact</h2>
      <p>
        {LEGAL_CONFIG.companyName}
        <br />
        {LEGAL_CONFIG.companyAddress}
        <br />
        <a href={`mailto:${LEGAL_CONFIG.legalEmail}`}>
          {LEGAL_CONFIG.legalEmail}
        </a>
      </p>
    </article>
  )
}
